/**
 * Build multipart body for POST /api/media/upload.
 *
 * Multer waits forever when the part is missing or the File stream is
 * detached/empty — DevTools shows Pending with no "[Media] Upload received".
 * Materialize bytes into a fresh File, then a fresh FormData, every attempt.
 */

export const MEDIA_UPLOAD_FIELD = 'file';

export function assertUploadableFile(file: File): void {
  if (!(file instanceof File)) {
    throw new Error('Selected media is not a file.');
  }
  if (!file.name?.trim()) {
    throw new Error('Selected media is missing a file name.');
  }
  if (!Number.isFinite(file.size) || file.size <= 0) {
    throw new Error('Selected media file is empty.');
  }
}

async function readFileBytes(file: File): Promise<ArrayBuffer> {
  if (typeof file.arrayBuffer === 'function') {
    return file.arrayBuffer();
  }
  // jsdom / older hosts: FileReader still yields the bytes.
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) resolve(reader.result);
      else reject(new Error('Could not read media file.'));
    };
    reader.onerror = () => reject(new Error('Could not read media file.'));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Read the picker File into an owned File so FormData always sends real bytes
 * (not a blob:/data: URL string, and not a consumed/detached stream).
 */
export async function materializeUploadFile(file: File): Promise<File> {
  assertUploadableFile(file);
  const bytes = await readFileBytes(file);
  if (bytes.byteLength === 0) {
    throw new Error('Selected media file is empty.');
  }
  return new File([bytes], file.name, {
    type: file.type || 'application/octet-stream',
    lastModified: file.lastModified,
  });
}

/** Fresh FormData with field `file` — never set Content-Type (browser boundary). */
export function buildMediaUploadFormData(file: File): FormData {
  assertUploadableFile(file);
  const formData = new FormData();
  formData.append(MEDIA_UPLOAD_FIELD, file, file.name);
  if (!formData.has(MEDIA_UPLOAD_FIELD)) {
    throw new Error('Upload FormData is missing the file field.');
  }
  const part = formData.get(MEDIA_UPLOAD_FIELD);
  if (!(part instanceof Blob) || part.size <= 0) {
    throw new Error('Upload FormData file part is empty.');
  }
  return formData;
}
